// CreateAppointment.tsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  requiredValidator,
  emailWOValueValidator,
  phoneValidator,
} from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormTextArea from "../../components/formFields/FormTextArea";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createAppointment,
  getAppointmentByID,
  updateAppointment,
} from "./services/appointment.service";
import { clearAppointmentDetails } from "./appointmentSlice";
import FormDatePicker from "../../components/formFields/FormDateField";

const TIME_SLOT_OPTIONS = [
  { label: "08:00 AM – 09:00 AM", value: "08:00 AM – 09:00 AM" },
  { label: "09:00 AM – 10:00 AM", value: "09:00 AM – 10:00 AM" },
  { label: "10:00 AM – 11:00 AM", value: "10:00 AM – 11:00 AM" },
  { label: "11:00 AM – 12:00 PM", value: "11:00 AM – 12:00 PM" },
  { label: "12:00 PM – 01:00 PM", value: "12:00 PM – 01:00 PM" },
  { label: "02:00 PM – 03:00 PM", value: "02:00 PM – 03:00 PM" },
  { label: "03:00 PM – 04:00 PM", value: "03:00 PM – 04:00 PM" },
  { label: "04:00 PM – 05:00 PM", value: "04:00 PM – 05:00 PM" },
  { label: "05:00 PM – 06:00 PM", value: "05:00 PM – 06:00 PM" },
];

const CreateAppointment: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const AppointmentID = location.state?.AppointmentID;

  const AppointmentDetail = useAppSelector(
    (state) => state.appointment.AppointmentDetail,
  );
  const loading = useAppSelector((state) => state.appointment.formLoading);

  // Uncomment and replace [] with real selector:
  // const doctors = useAppSelector((state) => state.doctor?.DoctorList || []);
  const doctors: any[] = [];

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [AppointmentDetail]);

  useEffect(() => {
    // dispatch(getAllActiveDoctors());
    if (AppointmentID) {
      dispatch(getAppointmentByID(AppointmentID));
    }
    return () => {
      dispatch(clearAppointmentDetails());
    };
  }, [AppointmentID]);

  const handleSubmit = async (values: any) => {
    const dateValue = values.Date
      ? values.Date instanceof Date
        ? values.Date.toISOString()
        : values.Date
      : null;

    const payload = {
      DoctorID: values.DoctorID ?? null,
      Fees: values.Fees ? Number(values.Fees) : 0,
      Date: dateValue,
      AvailableTimeSlot: values.AvailableTimeSlot ?? "",
      PatientName: values.PatientName ?? "",
      MobileNo: values.MobileNo ?? "",
      EmailID: values.EmailID ?? "",
      ReasonForVisit: values.ReasonForVisit ?? "",
      IsActive: values.IsActive === 1,
    };

    try {
      if (AppointmentID) {
        const response = await dispatch(
          updateAppointment({ ...payload, ID: AppointmentID }),
        );
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(getAppointmentByID(AppointmentID));
        }
      } else {
        const response = await dispatch(createAppointment(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/appointment");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={AppointmentDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 5, cols: 10 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                {/* ── Header ── */}
                <GridLayoutItem colSpan={3}>
                  <Typography.h4 style={{ margin: 0 }}>
                    {AppointmentID ? "Update Appointment" : "Add Appointment"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <ButtonWithLoading
                    label={AppointmentID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    onClick={() => navigate("/appointment")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>

                {/* ── Section: Doctor & Schedule ── */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Doctor &amp; Schedule
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="DoctorID"
                    label="Doctor"
                    placeholder="Select doctor"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={doctors.map((d: any) => ({
                      value: d.ID,
                      label: d.DoctorName,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="Date"
                    label="Appointment Date"
                    component={FormDatePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="AvailableTimeSlot"
                    label="Time Slot"
                    placeholder="Select time slot"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={TIME_SLOT_OPTIONS}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="Fees"
                    label="Consultation Fees (₹)"
                    placeholder="e.g., 500"
                    component={FormTextField}
                    type="number"
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => ({
                      value: status.value,
                      label: status.label,
                    }))}
                  />
                </GridLayoutItem>

                {/* ── Section: Patient Info ── */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Patient Information
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="PatientName"
                    label="Patient Name"
                    placeholder="Full name of patient"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="MobileNo"
                    label="Mobile No"
                    placeholder="Patient mobile number"
                    component={FormTextField}
                    validator={phoneValidator}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="EmailID"
                    label="Email ID"
                    placeholder="Patient email address"
                    component={FormTextField}
                    validator={emailWOValueValidator}
                  />
                </GridLayoutItem>

                {/* ── Section: Reason ── */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: 8,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Visit Details
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="ReasonForVisit"
                    label="Reason for Visit"
                    placeholder="Describe the reason for this appointment..."
                    component={FormTextArea}
                    rows={4}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                {/* ── Footer Buttons ── */}
                <GridLayoutItem
                  colSpan={4}
                  style={{
                    marginTop: 20,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 10,
                  }}
                >
                  <ButtonWithLoading
                    label={AppointmentID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    onClick={() => navigate("/appointment")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateAppointment;
